/**
 * linkPreview.js
 * Rich link preview cards for URLs in bot messages
 */

import { isVideoUrl } from './media/youtubeEmbed.js?v=302';

// Rich link preview cards
const URL_REGEX = /https?:\/\/[^\s<]+/gi;
const MAX_PREVIEWS = 3;

const EXTERNAL_LINK_SVG = `
  <svg class="link-preview-ext-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14L21 3" />
  </svg>
`;

/**
 * Enhance a rendered message bubble with link preview cards.
 * Idempotent: safe to call repeatedly.
 *
 * @param {HTMLElement} messageEl - The message bubble element (or any container containing .message-content)
 */
export function enhanceLinkPreviews(messageEl) {
  if (!messageEl || !(messageEl instanceof HTMLElement)) return;
  if (messageEl.querySelector('.link-preview-list')) return;

  const contentEl = messageEl.querySelector('.message-content') || messageEl;
  const urls = collectUrls(contentEl).filter((url) => !isVideoUrl(url));
  if (urls.length === 0) return;

  const list = document.createElement('div');
  list.className = 'link-preview-list';
  list.setAttribute('role', 'list');

  urls.slice(0, MAX_PREVIEWS).forEach((url) => {
    const card = createPreviewCard(url);
    list.appendChild(card);

    // Async metadata hydration; never block message rendering
    hydrateRichPreview(card, url);
  });

  // Render previews below the content, inside the bubble
  if (contentEl && contentEl.parentElement) {
    contentEl.insertAdjacentElement('afterend', list);
  } else {
    messageEl.appendChild(list);
  }
}

function collectUrls(rootEl) {
  const urls = [];
  const seen = new Set();

  const pushUrl = (candidate) => {
    const cleaned = cleanUrlCandidate(candidate);
    if (!cleaned) return;
    let u;
    try {
      u = new URL(cleaned);
    } catch {
      return;
    }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return;

    const normalized = u.href;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    urls.push(normalized);
  };

  // Prefer actual anchors after markdown parsing
  rootEl.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    if (!href) return;
    if (!href.startsWith('http://') && !href.startsWith('https://')) return;
    pushUrl(href);
  });

  // Fallback: regex scan for bare URLs in text
  const text = rootEl.textContent || '';
  const matches = text.match(URL_REGEX) || [];
  matches.forEach((m) => pushUrl(m));

  return urls;
}

function cleanUrlCandidate(candidate) {
  if (!candidate) return '';
  let s = String(candidate).trim();

  // Common markdown/punctuation wrappers
  s = s.replace(/^[\s\(\[\{"'<]+/, '');
  s = s.replace(/[\s\)\]\}"'>.,;:!?]+$/, '');

  return s;
}

function createPreviewCard(url) {
  const domain = extractDomain(url);

  const card = document.createElement('a');
  card.className = 'link-preview-card is-fallback';
  card.href = url;
  card.target = '_blank';
  card.rel = 'noopener noreferrer';
  card.setAttribute('role', 'listitem');

  const media = document.createElement('div');
  media.className = 'link-preview-media';

  const img = document.createElement('img');
  img.className = 'link-preview-image';
  img.alt = '';
  img.loading = 'lazy';
  img.decoding = 'async';

  const faviconWrap = document.createElement('div');
  faviconWrap.className = 'link-preview-favicon-wrap';

  const favicon = document.createElement('img');
  favicon.className = 'link-preview-favicon';
  favicon.alt = '';
  favicon.loading = 'lazy';
  favicon.decoding = 'async';
  favicon.referrerPolicy = 'no-referrer';
  favicon.src = getFaviconUrl(domain);

  faviconWrap.appendChild(favicon);
  media.appendChild(img);
  media.appendChild(faviconWrap);

  const body = document.createElement('div');
  body.className = 'link-preview-body';

  const top = document.createElement('div');
  top.className = 'link-preview-top';

  const domainEl = document.createElement('div');
  domainEl.className = 'link-preview-domain';
  domainEl.textContent = domain;

  const ext = document.createElement('div');
  ext.className = 'link-preview-ext';
  ext.innerHTML = EXTERNAL_LINK_SVG;

  top.appendChild(domainEl);
  top.appendChild(ext);

  const titleEl = document.createElement('div');
  titleEl.className = 'link-preview-title';
  titleEl.textContent = domain;

  const descEl = document.createElement('div');
  descEl.className = 'link-preview-desc';
  descEl.textContent = url;

  body.appendChild(top);
  body.appendChild(titleEl);
  body.appendChild(descEl);

  card.appendChild(media);
  card.appendChild(body);

  return card;
}

async function hydrateRichPreview(cardEl, url) {
  try {
    const meta = await fetchOpenGraph(url);
    if (!meta) return;

    const title = safeText(meta.title);
    const description = safeText(meta.description);
    const image = meta.image || '';

    // If we didn't get anything meaningful, keep the fallback
    if (!title && !description && !image) return;

    if (meta.siteName) {
      const domainEl = cardEl.querySelector('.link-preview-domain');
      if (domainEl) domainEl.textContent = safeText(meta.siteName) || domainEl.textContent;
    }

    const titleEl = cardEl.querySelector('.link-preview-title');
    const descEl = cardEl.querySelector('.link-preview-desc');
    if (titleEl && title) titleEl.textContent = title;
    if (descEl && description) descEl.textContent = description;

    if (image) {
      const imgEl = cardEl.querySelector('.link-preview-image');
      if (imgEl) {
        imgEl.src = image;
        imgEl.addEventListener(
          'error',
          () => {
            // Don't break the card if the image fails to load.
            cardEl.classList.remove('has-image');
          },
          { once: true }
        );
        cardEl.classList.add('has-image');
      }
    }

    cardEl.classList.remove('is-fallback');
  } catch {
    // CORS and parsing failures should silently fall back to basic card.
  }
}

async function fetchOpenGraph(url) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6500);

  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      redirect: 'follow',
      signal: controller.signal,
      headers: {
        Accept: 'text/html,application/xhtml+xml',
      },
    });

    if (!res.ok) return null;
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('text/html') && !contentType.includes('application/xhtml+xml')) return null;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc) return null;

    const og = (selector) => {
      const el = doc.querySelector(selector);
      return el ? (el.getAttribute('content') || '').trim() : '';
    };

    const title =
      og('meta[property="og:title"]') ||
      og('meta[name="twitter:title"]') ||
      (doc.querySelector('title') ? doc.querySelector('title').textContent.trim() : '');

    const description =
      og('meta[property="og:description"]') ||
      og('meta[name="description"]') ||
      og('meta[name="twitter:description"]');

    const siteName = og('meta[property="og:site_name"]') || '';

    let image = og('meta[property="og:image"]') || og('meta[name="twitter:image"]') || '';
    if (image) {
      try {
        image = new URL(image, url).href;
      } catch {
        image = '';
      }
    }

    return { title, description, siteName, image };
  } finally {
    clearTimeout(timeoutId);
  }
}

function safeText(value) {
  if (!value) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, 280);
}

function extractDomain(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return 'link';
  }
}

function getFaviconUrl(domain) {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
}

export { URL_REGEX, MAX_PREVIEWS };
